import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import set from 'lodash/set'
import { useEventSlug, useQuery } from 'hooks'
import { languageMapper } from 'components'
import FullPageLoading from 'components/FullPageLoading'
import { _, withCapture, validation } from 'utils'
import {
    EventQuery,
    EventQueryVariables,
    TimeFormat,
    UpdateEventInput,
} from 'generated/graphql'
import constants from 'constants/constants'
import { endOfDay, startOfDay } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { timeFormatMapper } from 'utils/datetime'
import FormTabs from '../forms/FormTabs'
import { showNotification, showErrorNotification } from '../notifications/notificationsSlice'
import { getChangedValues } from '../forms/utils'
import Setting from './tabs/Setting'
import { queryEvent } from './graphql/query'
import { updateEvent } from './graphql/mutation'
import Communication from './tabs/Communication'
import SuperAdmin from './tabs/SuperAdmin'
import { getDefaultLanguage } from './utils'
import { getEventData } from '../events/eventSlice'
import { changeLanguageImmediately, getEventPreview } from '../auth/eventPreviewSlice'

const transformDataForForm = (
    event: EventQuery['event']
): UpdateEventInput => (event && ({
    ...event,
    language: event.language || getDefaultLanguage(),
    startDate: new Date(event.startDate),
    endDate: new Date(event.endDate),
    timezone: event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeFormat: timeFormatMapper[event.timeFormat] || TimeFormat.Format24Hour,
    virtual: {
        goodiebagName: event.goodiebagName,
        // homeSpaceId is number, but type ID is string.
        homeSpaceId: event.homeSpace?.id as unknown as string,
        logo: event.virtualEventLogo,
        backgroundImage: event.backgroundImage,
        introText: event.textEventPreview,
        loginFormAddendum: event.loginFormAddendum,
        helpContent: event.helpContent,
        helpContentType: event.helpContentType,
        introVideo: event.introVideo,
        autostartIntroVideo: event.autostartVideo,
        startWithLoginTab: (event.startWithLoginTab ? 'login' : 'request') as unknown as boolean,
    },
    resellerConfig: {
        maxSpaces: event.maxSpaces,
        maxBooths: event.maxBooths,
        maxDaysWithNetworking: event.maxDaysWithNetworking,
        maxDurationInDays: event.maxDurationInDays,
        maxParticipants: event.maxParticipants,
        enableChatCarousel: event.enableChatCarousel,
    },
    organizationId: event.organization?.id as unknown as string,
}))

const initialForm: UpdateEventInput = {
    name: '',
    slug: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
    primaryBrandColor: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: getDefaultLanguage(),
    virtual: { introVideo: '' },
    resellerConfig: {
        maxSpaces: 5,
        maxBooths: 5,
        maxDaysWithNetworking: 1,
        maxDurationInDays: constants.isLite ? 31 : 1,
        maxParticipants: 100,
    }
}

const EventValidation = Yup.object().shape({
    name: Yup.string().required().min(3),
    slug: Yup
        .string()
        .required()
        .min(3)
        .matches(/^[a-z0-9_-]+$/, _('form.validation.invalid_slug', {}, false)),
    description: Yup.string(),
    startDate: Yup.date().required(),
    endDate: validation.endDateProgram,
    primaryBrandColor: validation.color,
    virtual: Yup.object().shape({
        introVideo: validation.videoEmbeddedUrl.nullable()
    }),
    resellerConfig: Yup.object().shape({
        maxSpaces: Yup.number().integer().min(0),
        maxBooths: Yup.number().integer().min(0),
        maxDaysWithNetworking: Yup.number().integer().min(0)
            .when(['maxDurationInDays'], (maxDurationInDays, schema) => maxDurationInDays && (
                schema
                    .max(
                        maxDurationInDays,
                        _('generalsettings.validation.networking_days_greater_opened_days', {}, false)
                    )
            )),
        maxDurationInDays: Yup.number().integer().min(0),
        maxParticipants: Yup.number().integer().min(0),
    })
})

const GeneralSettings = () => {
    const eventSlug = useEventSlug()
    const [isUploading, setIsUploading] = useState(false)
    const { data, requestStatus } = useQuery<
        EventQuery, EventQueryVariables
        >(queryEvent, { eventId: eventSlug })
    const [initialValues, setInitialValues] = useState<UpdateEventInput>(null)
    const dispatch = useDispatch()

    useEffect(() => {
        setInitialValues(transformDataForForm(data?.event))
    }, [data])

    return (requestStatus === 'succeeded' && data) ? (
        <Formik
            initialValues={initialValues || initialForm}
            enableReinitialize
            onSubmit={withCapture(async (values, { resetForm, setFieldError }) => {
                setIsUploading(true)
                const submitValues = getChangedValues(initialValues, values)
                // The homeSpaceId set to null when updating another field in virtual object.
                // its a bug in the library we use. We have to always include homeSpaceId in update data
                set(submitValues, 'virtual.homeSpaceId', values?.virtual?.homeSpaceId)
                delete submitValues.isActivationRequested
                if (submitValues.startDate) {
                    submitValues.startDate = utcToZonedTime(
                        startOfDay(submitValues.startDate), data.event.timezone
                    )
                }
                if (submitValues.endDate) {
                    submitValues.endDate = utcToZonedTime(
                        endOfDay(submitValues.endDate), data.event.timezone
                    )
                }
                const startWithLoginTab = submitValues?.virtual?.startWithLoginTab
                if (startWithLoginTab !== undefined) {
                    submitValues.virtual.startWithLoginTab = startWithLoginTab === 'login'
                }
                const response = await updateEvent(eventSlug, submitValues)
                if (response.data) {
                    const newValues = {
                        ...values,
                        virtual: {
                            ...values.virtual,
                            logo: response.data.updateEvent.virtualEventLogo,
                            backgroundImage: response.data.updateEvent.backgroundImage
                        }
                    }
                    resetForm({ values: newValues })
                    setInitialValues(newValues)
                    dispatch(showNotification({
                        type: 'success',
                        body: _('form.notification.update_success'),
                    }))
                    // If these below fields are updated, dispatch getEventData to update data in store
                    if (submitValues?.resellerConfig?.maxBooths !== undefined
                        || submitValues?.resellerConfig?.maxSpaces !== undefined
                        || submitValues?.resellerConfig?.maxParticipants !== undefined
                        || submitValues?.timeFormat
                        || submitValues?.published !== undefined
                    ) {
                        dispatch(getEventData(eventSlug))
                    }
                    // If language is updated, dispatch changeLanguageImmediately to update language of page
                    if (submitValues.language) {
                        dispatch(changeLanguageImmediately(languageMapper[submitValues.language]))
                    }
                    // If logo is updated, re-fetch getEventPreview to update logo in TopNav
                    if (submitValues?.virtual?.logo) {
                        dispatch(getEventPreview(eventSlug))
                    }
                } else {
                    const messages = response.errors
                    // eslint-disable-next-line max-len
                    const duplicateSlugError = 'duplicate key value violates unique constraint "events_event_slug'
                    if (messages.find((e) => e.includes(duplicateSlugError))) {
                        setFieldError('slug', _('form.validation.duplicate_slug'))
                    } else {
                        dispatch(showErrorNotification(messages))
                    }
                }

                setIsUploading(false)
            })}
            validationSchema={EventValidation}
        >
            <>
                <FormTabs
                    header={_('generalsettings.header')}
                    tabs={[
                        {
                            id: 'general',
                            name: _('generalsettings.tabs.setting'),
                            Component: () => <Setting />
                        },
                        {
                            id: 'communication',
                            name: _('generalsettings.tabs.communication'),
                            Component: Communication
                        },
                        data?.event?.currentUserHasResellerAccess && {
                            id: 'superAdmin',
                            name: _('generalsettings.tabs.superAdmin'),
                            Component: SuperAdmin
                        }
                    ].filter((e) => e)}
                    showHelpContent
                    helpContentField="virtual.helpContent"
                />
                {isUploading && <FullPageLoading isAbsolute />}
            </>
        </Formik>
    ) : (
        <FullPageLoading />
    )
}

export default GeneralSettings
